import { PastSparkForUser } from "@greenflagdate/shared";

/**
 * A past spark still needs matching when the user hasn't marked matching as
 * complete AND there are still attendees they haven't decided on, in the
 * category their account mode actually matches in.
 *
 * `friendsOnly` is not optional on purpose. The server derives every
 * participant's category from the viewer's friends_only flag, so for someone
 * in Friends Only mode EVERY participant comes back as "friend" — a hardcoded
 * `category === "dating"` here silently reports "nothing left to do" for that
 * entire cohort, which is what used to happen. Requiring the argument means a
 * new call site has to decide rather than inherit the dating default, and it
 * makes `list.some(hasUnfinishedMatching)` a type error rather than a bug
 * that quietly passes the array index in as the mode.
 *
 * This mirrors the predicate used by MatchFillProvider / Events.tsx so the
 * nudge, the widget and the "finished" split agree on what "unfinished" means.
 */
export function hasUnfinishedMatching(
  spark: PastSparkForUser,
  friendsOnly: boolean
): boolean {
  if (spark.matchesCompleted) return false;
  const wanted = friendsOnly ? "friend" : "dating";
  return spark.participants.some(
    (p) => p.category === wanted && p.status === null
  );
}

/**
 * Whether the Spark tab should show the post-event match interstitial instead
 * of the swipe deck. Callers use this to decide which to render; the
 * PostEventMatchNudge component independently returns null when this is false,
 * so the two never disagree. A plain function (not a hook) so it can be called
 * conditionally from render.
 */
export function hasPostEventMatchNudge(
  pastSparks: PastSparkForUser[] | undefined,
  friendsOnly: boolean
): boolean {
  return (pastSparks ?? []).some((spark) =>
    hasUnfinishedMatching(spark, friendsOnly)
  );
}
